import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon,
  PencilIcon,
  DocumentTextIcon,
  CalendarDaysIcon,
  UserIcon,
  BuildingOfficeIcon,
  CurrencyDollarIcon,
  TruckIcon,
  CreditCardIcon,
} from '@heroicons/react/24/outline';
import { useAppData } from '../../contexts/AppDataContext';

const formatCurrency = (value) => {
  const num = Number(value || 0);
  return `${num.toLocaleString()} DA`;
};

const getEntityName = (ent) => {
  if (!ent) return '';
  if (ent.name) return ent.name;
  const first = ent.firstName || '';
  const last = ent.lastName || '';
  return `${first} ${last}`.trim();
};

const OrderDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const {
    getOrderById,
    getCustomers,
    getSuppliers,
    getProducts,
  } = useAppData();

  const [order, setOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Resolve the order either from state, context or localStorage
  useEffect(() => {
    const resolveOrder = () => {
      try {
        // Priority 1: navigation state
        if (location.state && location.state.order) {
          setOrder(location.state.order);
          setIsLoading(false);
          return;
        }

        // Priority 2: context
        const ctxOrder = getOrderById(id);
        if (ctxOrder) {
          setOrder(ctxOrder);
          setIsLoading(false);
          return;
        }

        // Priority 3: localStorage (handles routes coming from demo list)
        const raw = localStorage.getItem('stockpro_orders') || localStorage.getItem('orders');
        if (raw) {
          const parsed = JSON.parse(raw);
          // Accept both demo shapes (customers vs suppliers) and ids that look like CMD-*
          const lsOrder = parsed.find((o) => o.id === id || o.orderNumber === id);
          if (lsOrder) {
            setOrder(lsOrder);
            setIsLoading(false);
            return;
          }
        }

        // Not found – show a friendly message
        toast.error("Commande introuvable");
        setIsLoading(false);
      } catch (error) {
        console.error('Error resolving order:', error);
        toast.error("Erreur lors du chargement de la commande");
        setIsLoading(false);
      }
    };

    resolveOrder();
  }, [id, location.state, getOrderById]);

  const customers = useMemo(() => getCustomers(), [getCustomers]);
  const suppliers = useMemo(() => getSuppliers(), [getSuppliers]);
  const products = useMemo(() => getProducts(), [getProducts]);

  const orderType = useMemo(() => {
    if (!order) return 'customers';
    // Also support demo orders coming from list (order.client / order.supplier)
    if (order.customerId || order.customerName || order.client) return 'customers';
    return 'suppliers';
  }, [order]);

  const entity = useMemo(() => {
    if (!order) return null;
    if (orderType === 'customers') {
      return (
        customers.find((c) => c.id === order.customerId) ||
        { name: order.customerName || order.client }
      );
    }
    return (
      suppliers.find((s) => s.id === order.supplierId) ||
      { name: order.supplierName || order.supplier }
    );
  }, [order, orderType, customers, suppliers]);

  const resolvedItems = useMemo(() => {
    if (!order || !Array.isArray(order.items)) return [];

    return order.items.map((item) => {
      const prod = products.find((p) => p.id === item.id || p.sku === item.sku);
      return {
        ...item,
        name: item.name || prod?.name || item.id,
        price: Number(item.price || prod?.price || 0),
        quantity: Number(item.quantity || 0),
        total: Number(item.total || (Number(item.quantity || 0) * Number(item.price || prod?.price || 0))),
      };
    });
  }, [order, products]);

  const totals = useMemo(() => {
    // If there are explicit items, compute from items
    const fromItems = resolvedItems.reduce((sum, it) => sum + (it.total || 0), 0);
    // Fallback to order.total when details come from demo list without items
    const subtotal = fromItems > 0 ? fromItems : Number(order?.total || 0);
    return { subtotal };
  }, [resolvedItems, order]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-white" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="p-6 bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20">
        <h2 className="text-2xl font-bold text-white mb-2">Commande introuvable</h2>
        <p className="text-white/60">Cette commande n'existe pas ou a été supprimée.</p>
        <button onClick={() => navigate('/orders')} className="mt-4 px-4 py-2 rounded-xl bg-white/10 text-white border border-white/20">Retour</button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/orders')}
            className="p-2 bg-white/10 rounded-full hover:bg-white/20 transition-colors"
          >
            <ArrowLeftIcon className="h-6 w-6 text-white" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-white">Détails de la Commande</h1>
            <p className="text-white/60 mt-1">{order.id || order.orderNumber}</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => navigate(`/orders/${id}/edit?type=${orderType}`)}
            className="px-4 py-2 bg-yellow-500/20 text-yellow-300 rounded-xl hover:bg-yellow-500/30 transition-colors flex items-center space-x-2"
          >
            <PencilIcon className="h-5 w-5" />
            <span>Modifier</span>
          </button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
          <div className="flex items-center space-x-3">
            <CalendarDaysIcon className="h-6 w-6 text-white/70" />
            <div>
              <p className="text-white/60 text-sm">Date</p>
              <p className="text-white font-medium">{order.date}</p>
            </div>
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
          <div className="flex items-center space-x-3">
            {orderType === 'customers' ? (
              <UserIcon className="h-6 w-6 text-white/70" />
            ) : (
              <BuildingOfficeIcon className="h-6 w-6 text-white/70" />
            )}
            <div>
              <p className="text-white/60 text-sm">{orderType === 'customers' ? 'Client' : 'Fournisseur'}</p>
              <p className="text-white font-medium">{getEntityName(entity)}</p>
            </div>
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
          <div className="flex items-center space-x-3">
            {orderType === 'customers' ? (
              <CreditCardIcon className="h-6 w-6 text-white/70" />
            ) : (
              <TruckIcon className="h-6 w-6 text-white/70" />
            )}
            <div>
              <p className="text-white/60 text-sm">{orderType === 'customers' ? 'Statut paiement' : 'Statut livraison'}</p>
              <p className="text-white font-medium">{orderType === 'customers' ? (order.paymentStatus || '—') : (order.deliveryStatus || '—')}</p>
            </div>
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
          <div className="flex items-center space-x-3">
            <CurrencyDollarIcon className="h-6 w-6 text-white/70" />
            <div>
              <p className="text-white/60 text-sm">Montant total</p>
              <p className="text-white font-medium">{formatCurrency(totals.subtotal)}</p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Items */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full divide-y divide-white/10">
            <thead className="bg-white/5">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">Article</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">Référence</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-white/70 uppercase tracking-wider">Prix</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-white/70 uppercase tracking-wider">Qté</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-white/70 uppercase tracking-wider">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {resolvedItems.map((it, idx) => (
                <tr key={`${it.id}-${idx}`} className="hover:bg-white/5">
                  <td className="px-6 py-4 text-white">{it.name}</td>
                  <td className="px-6 py-4 text-white/80">{it.sku || it.id}</td>
                  <td className="px-6 py-4 text-right text-white/80">{formatCurrency(it.price)}</td>
                  <td className="px-6 py-4 text-right text-white/80">{it.quantity}</td>
                  <td className="px-6 py-4 text-right text-white">{formatCurrency(it.total)}</td>
                </tr>
              ))}
              {resolvedItems.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center text-white/60">
                    Aucun article dans cette commande. Cliquez sur « Modifier » pour ajouter des articles.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="px-6 py-4 border-t border-white/10 flex justify-end">
          <div className="text-right">
            <div className="text-white/70 text-sm">Sous-total</div>
            <div className="text-white text-lg font-semibold">{formatCurrency(totals.subtotal)}</div>
          </div>
        </div>
      </div>

      {/* Footer actions */}
      <div className="flex justify-between">
        <div className="flex items-center space-x-2 text-white/60">
          <DocumentTextIcon className="h-5 w-5" />
          <span>Référence: {order.id || order.orderNumber}</span>
        </div>
        <div className="space-x-2">
          <button
            onClick={() => navigate(`/orders/${id}/edit?type=${orderType}`)}
            className="px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-300"
          >
            Modifier
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderDetail;

